import React, { useState, useEffect } from 'react';
import { Star, Lock, LogOut, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login?password=${encodeURIComponent(password)}`, {
        method: 'POST'
      });

      if (response.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminPassword', password);
        loadData(password);
      } else {
        setError('Nepareiza parole / Неверный пароль');
      }
    } catch (err) {
      setError('Kļūda. Mēģiniet vēlreiz.');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (pwd) => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/reviews?password=${encodeURIComponent(pwd)}`),
        fetch(`${BACKEND_URL}/api/admin/stats?password=${encodeURIComponent(pwd)}`)
      ]);

      if (reviewsRes.ok && statsRes.ok) {
        setReviews(await reviewsRes.json());
        setStats(await statsRes.json());
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    sessionStorage.removeItem('adminPassword');
  };

  useEffect(() => {
    const savedPassword = sessionStorage.getItem('adminPassword');
    if (savedPassword) {
      setPassword(savedPassword);
      setIsAuthenticated(true);
      loadData(savedPassword);
    }
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('lv-LV', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#1A1A1D] flex items-center justify-center p-4">
        <div className="bg-white p-8 md:p-12 rounded-lg shadow-xl max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#8B2942]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#8B2942]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1D]">
              Admin Panel
            </h1>
            <p className="text-[#6B6B6B] text-sm mt-2">
              Pavillon de Roze
            </p>
          </div>

          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Parole / Пароль"
              className="w-full p-4 border border-gray-200 rounded-lg focus:outline-none focus:border-[#8B2942] mb-4"
              data-testid="admin-password"
            />
            
            {error && (
              <p className="text-red-500 text-sm mb-4 text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading || !password}
              className="w-full py-4 bg-[#8B2942] text-white font-medium rounded-lg hover:bg-[#6B1F32] transition-colors disabled:opacity-50"
              data-testid="admin-login"
            >
              {loading ? 'Ielādē...' : 'Ieiet / Войти'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="font-['Playfair_Display'] text-xl text-[#1A1A1D]">
              Pavillon de Roze
            </h1>
            <p className="text-[#8B2942] text-xs">Admin Panel</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#6B6B6B] hover:text-[#8B2942] transition-colors"
          >
            <LogOut size={20} />
            <span className="hidden sm:inline">Iziet</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <p className="text-[#6B6B6B] text-sm mb-1">Kopā atsauksmju</p>
              <p className="text-3xl font-bold text-[#1A1A1D]">{stats.total}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <p className="text-[#6B6B6B] text-sm mb-1">Vidējais vērtējums</p>
              <div className="flex items-center gap-2">
                <p className="text-3xl font-bold text-[#1A1A1D]">{stats.average}</p>
                <Star className="w-6 h-6 text-[#D4AF37] fill-[#D4AF37]" />
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm col-span-2">
              <p className="text-[#6B6B6B] text-sm mb-3">Sadalījums / Распределение</p>
              <div className="flex gap-2">
                {[5, 4, 3, 2, 1].map((star) => (
                  <div key={star} className="flex-1">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs text-[#6B6B6B]">{star}</span>
                      <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#8B2942] rounded-full transition-all"
                        style={{
                          width: stats.total > 0
                            ? `${(stats.distribution[star] / stats.total) * 100}%`
                            : '0%'
                        }}
                      />
                    </div>
                    <p className="text-xs text-[#6B6B6B] mt-1 text-center">
                      {stats.distribution[star]}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Reviews List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1D]">
              Atsauksmes / Отзывы
            </h2>
          </div>
          
          {reviews.length === 0 ? (
            <div className="p-12 text-center">
              <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-[#6B6B6B]">Nav atsauksmju / Нет отзывов</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {reviews.map((review) => (
                <div key={review.id} className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={20}
                          className={
                            star <= review.rating
                              ? 'text-[#D4AF37] fill-[#D4AF37]'
                              : 'text-gray-200'
                          }
                        />
                      ))}
                    </div>
                    <span className="text-sm text-[#9CA3AF]">
                      {formatDate(review.created_at)}
                    </span>
                  </div>
                  {review.comment && (
                    <p className="text-[#1A1A1D] mt-2">{review.comment}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Admin;
